import {
  createTable,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  Table,
  useTableInstance,
} from "@tanstack/react-table";
import { Parser, transforms as InitialTransform } from "json2csv";
import React from "react";
import { CSVLink } from "react-csv";
import { useHistory } from "react-router-dom";
import { Button, Card, CardBody, Col, Row } from "shards-react";
import { Complaint } from "./type/Complaint.type";

export interface TableProps<T> {
  columns: any;
  data: T[];
  table: Table<any>;
  filename?: string;
}

function ReactTable({ data, filename }: TableProps<Complaint>) {
  const table = createTable().setRowType<Complaint>();

  const [sorting, setSorting] = React.useState<SortingState>([]);

  const history = useHistory();

  const columns = [
    table.createDataColumn("created", {
      cell: (info) => info.getValue(),
      header: () => "Created",
      footer: (props) => props.column.id,
    }),
    table.createDataColumn("id", {
      cell: (info) => (
        <div
          className="text-primary custom-link"
          onClick={() => {
            history.push(`/dashboard/complaintDetails/${info.getValue()}`);
          }}
        >
          {info.getValue()}
        </div>
      ),
      footer: (props) => props.column.id,
    }),

    table.createDataColumn("companyName", {
      size: 100,
      cell: (info) => <div className="d-flex">{info.getValue()}</div>,
      footer: (props) => props.column.id,
    }),
    table.createDataColumn("status", {
      cell: (info) => info.getValue(),
      header: () => "Status",
      footer: (props) => props.column.id,
    }),
    table.createDataColumn("sectors", {
      cell: (info) => info.getValue(),
      footer: (props) => props.column.id,
    }),
    table.createDataColumn("complaintType", {
      cell: (info) => info.getValue(),
      header: () => "Complaint Type",
      footer: (props) => props.column.id,
    }),
    table.createDataColumn("age", {
      cell: (info) => info.getValue(),
      footer: (props) => props.column.id,
    }),
    table.createDataColumn("ageGroup", {
      cell: (info) => info.getValue(),
      header: () => "Age Group",
      footer: (props) => props.column.id,
    }),
    table.createDataColumn("estimated_loss", {
      cell: (info) => info.getValue(),
      header: () => "Estimated Loss",
      footer: (props) => props.column.id,
    }),
  ];

  const getExportData = () => {
    const parser = new Parser();
    const fields = [
      "id",
      "agent.uid",
      "agent.displayName",
      "agent.email",
      "complainant.uid",
      "complainant.age",
      "complainant.ageGroup",
      "complainant.name",
      "complainant.email",
      "complainant.mobile",
      "companyName",
      "companyAddress",
      "sectors",
      "complaintType",
      "status",
      "created",
    ];
    const transforms = [InitialTransform.unwind({ paths: ["sectors"] })];

    const json2csvParser = new Parser({ fields, transforms });
    const exportData = json2csvParser.parse(data);

    return exportData;
  };

  const instance = useTableInstance(table, {
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
  });
  return (
    <Card small className="mb-4">
      <CardBody className="p-0 pb-3">
        <Row className="p-3">
          <Col>Total: {data.length}</Col>
          <Col>
            <CSVLink data={getExportData()} filename={filename}>
              <Button
                theme="primary"
                className="align-content-center float-right"
              >
                Download
              </Button>
            </CSVLink>
          </Col>
        </Row>

        <table className="table mb-0">
          <thead className="">
            {instance.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    className="px-6 text-left tracking-wider"
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        {...{
                          className: header.column.getCanSort()
                            ? "cursor-pointer select-none"
                            : "",
                          onClick: header.column.getToggleSortingHandler(),
                        }}
                      >
                        {header.renderHeader()}
                        {{
                          asc: " 🔼",
                          desc: " 🔽",
                        }[header.column.getIsSorted() as string] ?? null}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="">
            {instance.getRowModel().rows.map((row) => (
              <tr className="" key={row.id}>
                {row.getVisibleCells().map((cell) => {
                  return (
                    <td key={cell.id} role="cell" className="">
                      {cell.renderCell()}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </CardBody>
    </Card>
  );
}

export default ReactTable;
